import { useState, useEffect, useRef } from 'react'
import { canUseDOM } from '../lib/utils'

/**
 * Tracks the window dimensions with configurable debounce.
 * SSR safe — returns 0 for both dimensions on the server.
 *
 * @param {number} [debounceMs=100] - Debounce delay in milliseconds.
 * @returns {{ width: number, height: number }} Current window inner dimensions.
 */
export default function useWindowSize(debounceMs = 100) {
  const [size, setSize] = useState(() => ({
    width: canUseDOM ? window.innerWidth : 0,
    height: canUseDOM ? window.innerHeight : 0,
  }))
  const timeoutRef = useRef(null)

  useEffect(() => {
    const handleResize = () => {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => {
        setSize({ width: window.innerWidth, height: window.innerHeight })
      }, debounceMs)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(timeoutRef.current)
      window.removeEventListener('resize', handleResize)
    }
  }, [debounceMs])

  return { ...size }
}
